// buildRowParamConformance.js — per-ROW GAM/APS/Prebid param-conformance
// verdicts, for FullRequestAnalysisPane's "Check verdicts" section.
//
// AgentPanel.jsx computes GAM/APS/Prebid param conformance SESSION-WIDE: it
// merges a compiled Checks run's `prepared.results` (the spec layer — every
// baseline check that ran against the whole session) with an App Pack's
// `gamParamRules`/`apsParamRules`/`prebidParamRules` (the BU layer — see
// proxy-server/validation/{gamParamRules,apsParamRules,prebidParamRules}.js)
// via `buildParamConformance.js` / `buildApsParamConformance.js`. None of
// that reaches an individual row anywhere today.
//
// The spec layer needs a compiled suite + a full evaluateCheck run over
// every row — session-scoped machinery this pane must not pull in (its own
// docblock: "Presentation only... runs no new validation"). The BU layer,
// however, is genuinely row-scoped already: each rule is `{key/path,
// required, severity, pattern|values, platform}` and is evaluated against
// ONE row's own params/body — exactly what proxy-server/validation/*.js do,
// just server-side and session-aggregated. This file is the frontend LOCAL
// COPY of that same per-row evaluation (frontend/CLAUDE.md's established
// idiom for logic the server owns — mirrors validation/bidNoFill.js etc,
// just placed in utils/ per this feature's build contract), ported field-
// for-field from the proxy modules so a row's OWN verdicts are real, not
// fabricated.
//
// buildRowParamConformance(row, packs, opts) resolves the app pack, runs
// this row's params through that pack's rules, and hands the result through
// the EXACT SAME buildParamConformanceRows / buildApsParamConformanceRows
// builders AgentPanel.jsx uses — so ParamConformanceTable /
// ApsParamConformanceTable render it unchanged. Only the BU columns are
// ever populated (spec columns stay null — no fabricated spec verdict); the
// combined `verdict` therefore always comes from the BU layer.
//
// `packs` is the App Pack map (api/appPacks.js's `getPacks()` shape —
// `{ [slug]: pack }`), passed in by the caller so this file has no import
// side effects and stays a pure, testable function. A row whose app pack
// can't be resolved, or whose pack has no rules for its category, yields
// `{ gamParamRows: [], apsParamRows: [] }` — the pane's existing honest
// empty-state text takes over from there.
//
// Not ported: proxy-server/validation/prebidParamDerive.js's per-slot
// stored-request-id mapping rule (`imp.ext.gpid` -> expected stored request
// id via `stack.profiles[*].prebidMappings`) — that rule is synthesized
// server-side at evaluation time, never stored on the pack JSON this file
// reads, so it can't be replayed here. Any `prebidParamRules` entry
// carrying `mappings` is skipped rather than guessed at.
//
// Pure, ESM, never throws — ok with a junk/partial row or a malformed pack.

import { buildParamConformanceRows } from './buildParamConformance';
import { buildApsParamConformanceRows } from './buildApsParamConformance';
import { GAM_PARAM_DICTIONARY } from './gamParams';

/* ── shared low-level readers (ported from proxy-server/validation/*.js) ── */

function qsFromUrl(url) {
  try {
    return Object.fromEntries(new URL(String(url)).searchParams.entries());
  } catch {
    return {};
  }
}

/** Dot-path reader with array auto-indexing — mirrors validation/apsParamRules.js's readPath. */
function readPath(obj, path) {
  if (obj == null || !path) return undefined;
  const segs = String(path).split('.').filter(Boolean);
  let cur = [obj];
  for (const seg of segs) {
    const next = [];
    for (const node of cur) {
      if (node == null) continue;
      if (Array.isArray(node)) {
        for (const el of node) { if (el != null && el[seg] !== undefined) next.push(el[seg]); }
      } else if (typeof node === 'object' && node[seg] !== undefined) {
        next.push(node[seg]);
      }
    }
    cur = next;
  }
  const flat = [];
  for (const v of cur) { if (Array.isArray(v)) flat.push(...v); else flat.push(v); }
  const vals = flat.filter((v) => v !== undefined);
  if (vals.length === 0) return undefined;
  return vals.length === 1 ? vals[0] : vals;
}

/** Same shape as proxy-server's bodyOf — a row's parsed request body, or null. */
function bodyOf(row) {
  try {
    const rbp = row && row.requestBodyParsed;
    if (rbp && typeof rbp === 'object') return rbp;
    const rb = row && row.requestBody;
    if (rb && typeof rb === 'object') return rb;
    if (typeof rb === 'string' && rb.trim().startsWith('{')) return JSON.parse(rb);
  } catch { /* not JSON */ }
  return null;
}

/** The parsed envelope's request block (docs/parsed-envelope-spec.md §3.2), or null. */
function envelopeOf(row) {
  const p = row && row.validData && row.validData.parsed;
  return (p && typeof p === 'object' && p.request && typeof p.request === 'object') ? p.request : null;
}

function requestTypeOf(row) {
  return String((row && (row.requestType || row.type)) || '').toLowerCase();
}

function describeExpected(rule) {
  if (!rule) return 'present';
  if (rule.pattern) return `matches /${rule.pattern}/`;
  if (Array.isArray(rule.values) && rule.values.length) return `one of ${rule.values.join(', ')}`;
  if (Array.isArray(rule.range) && rule.range.length === 2) return `between ${rule.range[0]} and ${rule.range[1]}`;
  return 'present';
}

function familyOfPlatformId(id) {
  const p = String(id || '').toLowerCase();
  if (p.startsWith('droid') || p.startsWith('android')) return 'android';
  if (p.startsWith('ios')) return 'ios';
  return null;
}

/* ── GAM (top-level query params — proxy-server/validation/gamParamRules.js) ── */

function platformFromIu(iu) {
  if (!iu || typeof iu !== 'string') return null;
  const parts = iu.replace(/^\//, '').split('/').filter(Boolean);
  return parts[2] || null;
}

function gamRuleApplies(rule, params) {
  if (rule.platform === undefined || rule.platform === null) return true;
  return platformFromIu(params.iu) === rule.platform;
}

function gamRuleFails(rule, val) {
  const missing = val === undefined || val === null || val === '';
  if (missing) return rule.required === true;
  const strVal = String(val);
  if (rule.pattern) {
    let re;
    try { re = new RegExp(rule.pattern); } catch { return false; } // uncompilable pattern: skip, never fail
    return !re.test(strVal);
  }
  if (Array.isArray(rule.values) && rule.values.length) {
    return !rule.values.map(String).includes(strVal);
  }
  return false;
}

/** { [gamParamKey]: {eligible, failing, severity, expected} } for ONE row. */
function summarizeGamRulesForRow(row, rules) {
  try {
    if (requestTypeOf(row) !== 'google') return {};
    const list = Array.isArray(rules) ? rules.filter((r) => r && r.key) : [];
    if (!list.length) return {};
    const params = qsFromUrl(row && row.url);
    const byKey = {};
    for (const rule of list) {
      if (!gamRuleApplies(rule, params)) continue; // e.g. a platform-scoped rule this row isn't
      const val = params[rule.key];
      byKey[rule.key] = {
        eligible: 1,
        failing: gamRuleFails(rule, val) ? 1 : 0,
        severity: rule.severity || 'warn',
        expected: describeExpected(rule),
      };
    }
    return byKey;
  } catch {
    return {};
  }
}

/* ── APS (proprietary display body — proxy-server/validation/apsParamRules.js) ── */

const APS_ENVELOPE_PATHS = {
  appId: 'appId',
  adsdk: 'sdkVersion',
  'pkg.pn': 'pkgName',
  'pkg.vn': 'pkgVersion',
  'pj.mediationName': 'mediationName',
  'pj.cstm.cturl': 'contentUrl',
  'slots.slot': 'slots.slotId',
  'dinfo.osVersion': 'device.osv',
  'dinfo.osv': 'device.osv',
  'dinfo.model': 'device.model',
  'dinfo.os': 'device.os',
  idfa: 'device.ifa',
  'dinfo.ifa': 'device.ifa',
  'pj.att': 'device.att',
  us_privacy: 'consent.usp',
  gpp: 'consent.gpp',
  gdpr_consent: 'consent.tcf',
};

function readApsValue(key, body, envelope) {
  const fromBody = body ? readPath(body, key) : undefined;
  if (fromBody !== undefined) return fromBody;
  const envPath = APS_ENVELOPE_PATHS[key];
  if (!envPath || !envelope) return undefined;
  return readPath(envelope, envPath);
}

function isApsDisplayShaped(body, envelope) {
  if (envelope && envelope.appId) return true;
  if (!body) return false;
  if (Array.isArray(body.imp)) return false; // APS OTT/C2S OpenRTB — different field set entirely
  return !!(body.appId || Array.isArray(body.slots) || body.pkg || body.isDTBMobile);
}

function apsPlatformFamily(body, envelope) {
  const os = String(
    (envelope && envelope.device && envelope.device.os)
    || (body && body.dinfo && body.dinfo.os)
    || ''
  ).toLowerCase();
  if (!os) return null;
  if (os.includes('android')) return 'android';
  if (os.includes('ios') || os.includes('iphone') || os.includes('ipad')) return 'ios';
  return null;
}

function apsRuleApplies(rule, platform) {
  if (rule.platform === undefined || rule.platform === null) return true;
  const want = familyOfPlatformId(rule.platform);
  if (!want || !platform) return false; // unknown platform: can't judge a platform-scoped rule
  return platform === want;
}

function apsRuleFails(rule, raw) {
  const vals = (raw === undefined || raw === null)
    ? []
    : (Array.isArray(raw) ? raw : [raw]).filter((v) => v !== undefined && v !== null && v !== '');
  if (!vals.length) return rule.required === true;
  let re = null;
  if (rule.pattern) {
    try { re = new RegExp(rule.pattern); } catch { return false; }
  }
  const allowed = (!re && Array.isArray(rule.values) && rule.values.length) ? rule.values.map(String) : null;
  if (!re && !allowed) return false; // presence-only rule, and it's present
  return vals.map(String).some((v) => (re ? !re.test(v) : !allowed.includes(v)));
}

/** { [apsBodyKey]: {eligible, failing, severity, expected} } for ONE row, plus its platform family. */
function summarizeApsRulesForRow(row, rules) {
  try {
    if (requestTypeOf(row) !== 'amazon') return { byKey: {}, platform: null };
    const body = bodyOf(row);
    const envelope = envelopeOf(row);
    const platform = apsPlatformFamily(body, envelope);
    if (!isApsDisplayShaped(body, envelope)) return { byKey: {}, platform };

    const list = Array.isArray(rules) ? rules.filter((r) => r && r.key) : [];
    const byKey = {};
    for (const rule of list) {
      if (!apsRuleApplies(rule, platform)) continue;
      const raw = readApsValue(rule.key, body, envelope);
      byKey[rule.key] = {
        eligible: 1,
        failing: apsRuleFails(rule, raw) ? 1 : 0,
        severity: rule.severity || 'warn',
        expected: describeExpected(rule),
      };
    }
    return { byKey, platform };
  } catch {
    return { byKey: {}, platform: null };
  }
}

/* ── Prebid (ORTB body — proxy-server/validation/prebidParamRules.js) ── */

const PREBID_ENVELOPE_PATHS = {
  tmax: 'tmax',
  cur: 'cur',
  test: 'test',
  at: 'at',
  'app.bundle': 'app.bundle',
  'app.name': 'app.name',
  'app.ver': 'app.ver',
  'app.storeurl': 'app.storeurl',
  'app.publisher.id': 'app.publisherId',
  'app.content.url': 'app.contentUrl',
  'source.tid': 'source.tid',
  'regs.coppa': 'regs.coppa',
  'regs.gpp': 'regs.gpp',
  'regs.ext.us_privacy': 'regs.usp',
  'device.ifa': 'device.ifa',
  'device.lmt': 'device.lmt',
  'device.osv': 'device.osv',
  'device.model': 'device.model',
};

const PREBID_ENVELOPE_IMP_PATHS = {
  id: 'id',
  secure: 'secure',
  instl: 'instl',
  'banner.api': 'bannerApi',
  'ext.gpid': 'gpid',
  'ext.data.pbadslot': 'gpid',
  'ext.prebid.storedrequest.id': 'storedRequestId',
  bidfloor: 'bidfloor',
  bidfloorcur: 'bidfloorcur',
};

function prebidImpsOf(body, envelope) {
  const fromBody = body && Array.isArray(body.imp) ? body.imp : null;
  const fromEnv = envelope && Array.isArray(envelope.imps) ? envelope.imps : null;
  const n = Math.max(fromBody ? fromBody.length : 0, fromEnv ? fromEnv.length : 0);
  const out = [];
  for (let i = 0; i < n; i += 1) {
    out.push({ body: fromBody ? fromBody[i] : null, envelope: fromEnv ? fromEnv[i] : null });
  }
  return out;
}

function readPrebidTop(path, body, envelope) {
  const fromBody = body ? readPath(body, path) : undefined;
  if (fromBody !== undefined) return fromBody;
  const envPath = PREBID_ENVELOPE_PATHS[path];
  if (!envPath || !envelope) return undefined;
  return readPath(envelope, envPath);
}

function readPrebidImp(sub, imp) {
  const fromBody = imp.body ? readPath(imp.body, sub) : undefined;
  if (fromBody !== undefined) return fromBody;
  const envField = PREBID_ENVELOPE_IMP_PATHS[sub];
  if (!envField || !imp.envelope) return undefined;
  const v = imp.envelope[envField];
  return v === undefined ? undefined : v;
}

function isMissing(val) {
  return val === undefined || val === null || val === '';
}

function prebidValueFails(rule, val) {
  if (isMissing(val)) return rule.required === true;
  if (Array.isArray(val)) return val.some((v) => prebidValueFails(rule, v));
  const strVal = String(val);
  if (rule.pattern) {
    let re;
    try { re = new RegExp(rule.pattern); } catch { return false; }
    return !re.test(strVal);
  }
  if (Array.isArray(rule.values) && rule.values.length) {
    return !rule.values.map(String).includes(strVal);
  }
  if (Array.isArray(rule.range) && rule.range.length === 2) {
    const n = Number(val);
    if (!Number.isFinite(n)) return true;
    const [min, max] = rule.range.map(Number);
    if (!Number.isFinite(min) || !Number.isFinite(max)) return false; // malformed range: no claim
    return !(n >= min && n <= max);
  }
  return false;
}

/**
 * { [ortbPath]: {eligible, failing, severity, expected} } for ONE row.
 * Rules carrying `mappings` (the derived per-slot stored-request-id rule —
 * see file header) are skipped, never guessed at.
 */
function summarizePrebidRulesForRow(row, rules) {
  try {
    if (requestTypeOf(row) !== 'prebid') return {};
    const list = Array.isArray(rules) ? rules.filter((r) => r && r.path && !r.mappings) : [];
    if (!list.length) return {};

    const body = bodyOf(row);
    const envelope = envelopeOf(row);
    const imps = prebidImpsOf(body, envelope);

    const byPath = {};
    for (const rule of list) {
      const path = String(rule.path);
      if (!path.startsWith('imp.')) {
        const val = readPrebidTop(path, body, envelope);
        byPath[path] = {
          eligible: 1,
          failing: prebidValueFails(rule, val) ? 1 : 0,
          severity: rule.severity || 'warn',
          expected: describeExpected(rule),
        };
        continue;
      }
      if (!imps.length) continue; // no imps to evaluate — rule scope didn't match this row
      const sub = path.slice('imp.'.length);
      let failing = false;
      for (const imp of imps) {
        if (prebidValueFails(rule, readPrebidImp(sub, imp))) failing = true;
      }
      byPath[path] = {
        eligible: 1,
        failing: failing ? 1 : 0,
        severity: rule.severity || 'warn',
        expected: describeExpected(rule),
      };
    }
    return byPath;
  } catch {
    return {};
  }
}

/* ── app-pack resolution (api/appPacks.js's findPackByIu, ported so this file has no side effects) ── */

function findPackByIuIn(packs, iu) {
  const parts = String(iu || '').split('/').filter(Boolean);
  if (parts.length < 2) return null;
  const [networkId, brandCode] = parts;
  for (const pack of Object.values(packs || {})) {
    if (!pack || pack.enabled === false) continue;
    const identity = pack.identity || {};
    if (identity.networkId && identity.networkId === networkId
      && Array.isArray(identity.brandCodes) && identity.brandCodes.includes(brandCode)) {
      return pack;
    }
  }
  return null;
}

/**
 * APS (and Prebid, as a fallback) carry no `iu`-shaped identifier the way a
 * GAM request does. Rather than guess which of several configured App Packs
 * a row belongs to, only resolve when there's exactly one enabled pack that
 * even has rules for this category — ambiguous cases fall through to the
 * honest "no pack" empty state instead of attaching the wrong pack's rules.
 */
function soleEnabledPackWithRules(packs, ruleField) {
  const candidates = Object.values(packs || {}).filter(
    (p) => p && p.enabled !== false && Array.isArray(p[ruleField]) && p[ruleField].length
  );
  return candidates.length === 1 ? candidates[0] : null;
}

/** The GAM ad-unit path a Prebid imp's gpid carries — same value an `iu` would be. */
function gpidFromRow(row, body, envelope) {
  const fromBody = body
    ? (readPath(body, 'imp.ext.gpid') || readPath(body, 'imp.ext.data.pbadslot'))
    : undefined;
  if (fromBody) return String(Array.isArray(fromBody) ? fromBody[0] : fromBody);
  const imps = envelope && Array.isArray(envelope.imps) ? envelope.imps : [];
  const withGpid = imps.find((i) => i && i.gpid);
  return withGpid ? String(withGpid.gpid) : null;
}

function filterDictToKeys(dictionary, keys) {
  if (!Array.isArray(dictionary) || !keys.length) return [];
  const keySet = new Set(keys);
  return dictionary.filter((d) => d && typeof d.key === 'string' && keySet.has(d.key));
}

/**
 * buildRowParamConformance(row, packs, opts) -> { gamParamRows, apsParamRows, sessionPlatform }
 *
 * `packs` — the App Pack map, `api/appPacks.js`'s `getPacks()` shape
 * (`{ [slug]: pack }`). `opts.apsDictionary` (array) / `opts.prebidDictionary`
 * (`{ params, aliases }`) are the same fetched knowledge packs AgentPanel.jsx
 * already pulls via `getApsParamDictionary()` / `getPrebidParamDictionary()`
 * — optional; omitting them just means matched rows render their raw
 * key/path instead of a friendly label.
 *
 * `gamParamRows` carries GAM rows for a `google` row OR Prebid rows for a
 * `prebid` row (never both — a row is only ever one), matching
 * AgentPanel.jsx's own precedent of rendering Prebid through the GAM pass's
 * ParamConformanceTable rather than a third table.
 */
export function buildRowParamConformance(row, packs, opts = {}) {
  try {
    const packMap = (packs && typeof packs === 'object') ? packs : {};
    const requestType = requestTypeOf(row);

    if (requestType === 'google') {
      const params = qsFromUrl(row && row.url);
      const pack = params.iu ? findPackByIuIn(packMap, params.iu) : null;
      const byKey = summarizeGamRulesForRow(row, pack ? pack.gamParamRules : null);
      const buSummary = Object.keys(byKey).length ? byKey : null;
      const gamParamRows = buildParamConformanceRows([], GAM_PARAM_DICTIONARY, buSummary);
      return { gamParamRows, apsParamRows: [], sessionPlatform: null };
    }

    if (requestType === 'amazon') {
      const pack = soleEnabledPackWithRules(packMap, 'apsParamRules');
      const { byKey, platform } = summarizeApsRulesForRow(row, pack ? pack.apsParamRules : null);
      const buSummary = Object.keys(byKey).length ? byKey : null;
      const dict = filterDictToKeys(opts.apsDictionary, Object.keys(byKey));
      const apsParamRows = buildApsParamConformanceRows([], dict, buSummary, platform);
      return { gamParamRows: [], apsParamRows, sessionPlatform: platform };
    }

    if (requestType === 'prebid') {
      const body = bodyOf(row);
      const envelope = envelopeOf(row);
      const gpid = gpidFromRow(row, body, envelope);
      const pack = (gpid && findPackByIuIn(packMap, gpid)) || soleEnabledPackWithRules(packMap, 'prebidParamRules');
      const byPath = summarizePrebidRulesForRow(row, pack ? pack.prebidParamRules : null);
      const buSummary = Object.keys(byPath).length ? byPath : null;
      const prebidDict = (opts.prebidDictionary && typeof opts.prebidDictionary === 'object')
        ? (opts.prebidDictionary.params || opts.prebidDictionary)
        : {};
      const gamParamRows = buildParamConformanceRows([], prebidDict, buSummary);
      return { gamParamRows, apsParamRows: [], sessionPlatform: null };
    }

    return { gamParamRows: [], apsParamRows: [], sessionPlatform: null };
  } catch {
    return { gamParamRows: [], apsParamRows: [], sessionPlatform: null };
  }
}

export default buildRowParamConformance;
